import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMeQuery } from '@platform/state/api';
import { Icon } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { cx } from '@platform/core/lib/cx';
import { useInbox } from '@platform/features/users/inbox/useInbox';
import { useSignOut } from '../auth/useSignOut';
import { appRoutes } from './appRoutes';
import { initials } from './initials';
import type { IMenuControl } from './IMenuControl';
import { MenuOverlay } from './MenuOverlay';
import { useLanguage } from './useLanguage';
import { useMenuKeys } from './useMenuKeys';
import { websiteUrl } from './websiteUrl';

/**
 * Аватар і меню облікового запису (UIUX top-bar, прототип v4 G:L273–290): біле коло 34px із синіми ініціалами; меню 260px — ім'я й e-mail,
 * Вхідні з лічильником непрочитаних, Профіль, Довідковий центр, Налаштування (Профіль › Вигляд — колірні схеми), Повернутися на вебсайт,
 * Мова УКР | ENG, Вийти.
 */
export function ProfileMenu({ open, onToggle, onClose }: IMenuControl) {
  const { t } = useTranslation();
  const button = useRef<HTMLButtonElement>(null);
  const { menu, onKeyDown } = useMenuKeys<HTMLDivElement>(open, onClose, button);
  const [lang, setLang] = useLanguage();
  const signOut = useSignOut();
  const { data: me } = useMeQuery();
  const { storedUnread: unread } = useInbox();
  const name = me?.displayName ?? '';
  const item = (icon: string, text: string) => <><Icon name={icon} size={16} className="tb-profile__icon" /><span className="tb-profile__label">{text}</span></>;
  return (
    <div className="tb-anchor">
      <button ref={button} type="button" className="tb-avatar" title={name} aria-label={name} aria-haspopup="menu" aria-expanded={open} onClick={onToggle}>{initials(name)}</button>
      {open && (
        <>
          <MenuOverlay onClose={onClose} />
          <div ref={menu} role="menu" aria-label={t('shell.account')} className="tb-dd tb-dd--right tb-profile" onKeyDown={onKeyDown}>
            <div className="tb-profile__head">
              <span className="tb-profile__avatar" aria-hidden="true">{initials(name)}</span>
              <span className="tb-profile__who"><strong className="tb-profile__name">{name}</strong><span className="tb-profile__email">{me?.email}</span></span>
            </div>
            <Link role="menuitem" className="tb-profile__item" to={routes.inbox()} onClick={onClose}>
              {item('inbox', t('shell.inbox'))}
              {unread > 0 && <span className="tb-profile__pill">{unread}</span>}
            </Link>
            <Link role="menuitem" className="tb-profile__item" to={appRoutes.profile()} onClick={onClose}>{item('user', t('shell.profile'))}</Link>
            <a role="menuitem" className="tb-profile__item" href={websiteUrl(lang, 'platform/help/index.html')} target="_blank" rel="noopener noreferrer" onClick={onClose}>{item('circle-help', t('shell.helpCentre'))}</a>
            <Link role="menuitem" className="tb-profile__item" to={appRoutes.profile('appearance')} onClick={onClose}>{item('settings', t('shell.settings'))}</Link>
            <a role="menuitem" className="tb-profile__item" href={websiteUrl(lang)} title={t('shell.backToSiteTitle')}>{item('external-link', t('shell.backToSite'))}</a>
            <button type="button" role="menuitem" className="tb-profile__item" title={t('shell.languageTitle')} onClick={() => setLang(lang === 'uk' ? 'en' : 'uk')}>
              {item('globe', t('shell.language'))}
              <span className="tb-lang" aria-hidden="true">
                <span className={cx('tb-lang__opt', lang === 'uk' && 'is-on')}>{t('shell.langUk')}</span>
                <span className={cx('tb-lang__opt', lang === 'en' && 'is-on')}>{t('shell.langEn')}</span>
              </span>
            </button>
            <span className="tb-dd__sep" role="separator" />
            <button type="button" role="menuitem" className="tb-profile__item is-danger" onClick={() => { onClose(); signOut(); }}>{item('log-out', t('shell.signOut'))}</button>
          </div>
        </>
      )}
    </div>
  );
}
